<template>
  <div
    :id="'bar-chart-container-' + dataName"
    class="bar-chart-container d-flex justify-content-center"
  >
    <div
      :id="'bar-chart-' + dataName"
      class="bar-chart"
      :class="options.rotateXAxisLabels ? 'rotated-x-axis' : ''"
    ></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import { primaryColor, segmentsColors } from '@/puls-color-theme'

export default {
  name: 'BarChart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    dataName: {
      type: String,
      default: ''
    },
    numberOfResponses: {
      type: Number,
      default: 0
    },
    options: {
      type: Object,
      default: () => {}
    },
    theme: {
      type: String,
      default: 'default'
    }
  },
  data() {
    return {
      // margin: { top: 30, right: 30, bottom: 60, left: 50 },
      // margin: { top: 10, right: 30, bottom: 60, left: 40 },
      margin: {
        top: 10,
        right: 30,
        // bottom: this.options.rotateXAxisLabels ? 230 : 60,
        bottom: 60,
        left: 40
      },
      transitionTime: 350,
      svg: null,
      line: null,
      x: null,
      y: null
    }
  },
  computed: {
    absoluteNumbers() {
      return !this.options.value
    },
    amountFormatter() {
      if (this.absoluteNumbers) {
        return (amount) => amount
      } else {
        return d3.format('.0%')
      }
    },
    color() {
      return d3
        .scaleOrdinal()
        .domain(Object.keys(this.stack))
        .range(segmentsColors)
    },
    dataReady() {
      if (this.stack) {
        return this.data
      } else {
        return this.data
      }
    },
    domain() {
      if (this.stack) {
        const sums = this.dataReady.map((k) => {
          return Object.values(k).reduce((res, n) => {
            if (Number.isInteger(n)) {
              res += n
            }
            return res
          }, 0)
        })
        const max = Math.max(...sums)
        return [this.options.domain ? this.options.domain[0] : 0, max]
      } else {
        // defaults so that the highest bar is always at top
        const max = d3.max(this.dataReady, (d) => {
          return d.value
        })
        if (max === 0) {
          return [0, 10] // fallback if all are empty
        } else {
          return this.options && this.options.domain
            ? this.options.domain
            : [
                0,
                d3.max(this.dataReady, (d) => {
                  return d.value
                })
              ]
        }
      }
    },
    height() {
      const defaultHeight = this.options.height
        ? this.options.height - this.margin.top - this.margin.bottom
        : 380 // 280
      return defaultHeight - this.margin.top - this.margin.bottom
    },
    stack() {
      return this.options.stack
    },
    ticks() {
      if (this.stack) {
        const high = this.domain[1]
        if (high < 10) {
          return high
        } else {
          return 4
        }
      } else {
        const max = d3.max(this.dataReady, (d) => {
          return d.value
        })
        let ticks = 10 // default
        if (max > 10) {
          ticks = 4
        }
        if (max < 10) {
          ticks = max
        }
        return this.options && this.options.ticks ? this.options.ticks : ticks
      }
    },
    width() {
      const defaultWidth = this.options.width
        ? this.options.width - this.margin.left - this.margin.right
        : 640
      return defaultWidth - this.margin.left - this.margin.right
    }
  },
  watch: {
    options: function (newData, oldData) {
      if (
        JSON.stringify(Object.keys(newData?.stack || {})) !==
        JSON.stringify(Object.keys(oldData?.stack || {}))
      ) {
        this.clearOldStack(Object.keys(oldData?.stack || {}))
      }
      this.updateChart()
    },
    data: function (newData, oldData) {
      this.updateChart()
    },
    dataName: function (newData, oldData) {
      if (newData && newData !== oldData) {
        this.initChart()
      }
    }
  },
  mounted() {
    this.initChart()
    this.updateChart()
  },
  beforeUnmount() {
    d3.select(`.tooltip-bar-chart-container-${this.dataName}`).remove()
  },
  methods: {
    addMouseTooltip(element) {
      element
        .on('mouseover', this.onMouseOver)
        .on('mousemove', this.onMouseMove)
        .on('mouseleave', this.onMouseLeave)
    },
    colorForBarChartValue(choiceNumber) {
      if (!Number.isInteger(choiceNumber)) {
        return primaryColor
      } else {
        const isEnps = this.dataName.includes('enps')
        const maxForRed = isEnps ? 7 : 4
        const minOrEqualForGreen = isEnps ? 9 : 7
        if (this.theme === 'monochrome' || choiceNumber === null) {
          return this.$resultColors.brand
        } else {
          if (choiceNumber < maxForRed) {
            return this.$resultColors.red
          } else if (choiceNumber >= minOrEqualForGreen) {
            return this.$resultColors.green
          } else {
            return this.$resultColors.yellow
          }
        }
      }
    },
    initChart() {
      const that = this
      // tooltip
      d3.select('body')
        .append('div')
        .attr('class', `tooltip tooltip-bar-chart-container-${this.dataName}`)
        .style('opacity', 0.0)
        .style('pointer-events', 'none')
        .style('min-width', '300px;')
        .style('z-index', 4000)

      // append the svg object to the body of the page
      this.svg = d3
        .select(`#bar-chart-${this.dataName}`)
        .append('svg')
        // .attr('width', this.width)
        // .attr('height', this.height)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr(
          'viewBox',
          `0 0 ${this.width + this.margin.left + this.margin.right} ${
            this.height + this.margin.top + this.margin.bottom
          }`
        )
        .classed('svg-content-responsive', true)
        .append('g')
        .attr(
          'transform',
          'translate(' + this.margin.left + ',' + this.margin.top + ')'
        )

      // Add X
      this.x = d3
        .scaleBand()
        .range([0, this.width])
        .domain(
          this.dataReady.map(function (d) {
            return d.name
          })
        )
        .padding(0.2)

      const xAxisGroup = this.svg
        .append('g')
        .attr(
          'class',
          'x barchart-axis' +
            (this.options.largeFont || this.options.fullScreen
              ? ' large-font'
              : ' normal-font') +
            (this.data.length > 10 ? ' many-options' : '')
        )
        .attr('transform', 'translate(0,' + this.height + ')')
        .call(d3.axisBottom(this.x))

      // Apply rotation only if options.rotateXAxis is true
      // Hack the tooltip to change the text to the label title for rotated labels
      if (this.options.rotateXAxisLabels) {
        const that = this
        const xAxisText = xAxisGroup
          .selectAll('text')
          .style('text-anchor', 'start')
          .attr('dx', '1em')
          .attr('dy', '0em')
          .each(function (d) {
            d3.select(this)
              .on('mouseover', () => {
                const tooltip = d3.select(
                  `.tooltip-bar-chart-container-${that.dataName}`
                )
                tooltip.transition().duration(200).style('opacity', 0.95)
                tooltip.html(`<div class="p-2 tooltip-bg">${d}</div>`)
              })
              .on('mousemove', that.onMouseMove)
              .on('mouseleave', that.onMouseLeave)
          })
        xAxisText.attr('transform', 'rotate(70)')
      }

      // Add Y
      this.y = d3.scaleLinear().domain(this.domain).range([this.height, 0])
      this.svg
        .append('g')
        .attr(
          'class',
          'y barchart-axis' +
            (this.options.largeFont || this.options.fullScreen
              ? ' large-font'
              : ' normal-font')
        )
        .call(
          d3
            .axisLeft(this.y)
            .ticks(this.ticks)
            .tickFormat(function (d) {
              if (d >= 1000) {
                d = d / 1000 + 'K'
              }
              return d
            })
        )

      // Grid
      var yGrid = d3.scaleLinear().domain(this.domain).range([this.height, 0])
      var yAxis = d3
        .axisLeft(yGrid)
        .ticks(this.ticks)
        .tickSize(-this.width)
        .tickFormat('')

      this.svg.append('g').attr('class', 'barchart-grid').call(yAxis)

      // Init bars
      if (this.stack) {
        // var stackedData = d3.stack()
        //   .keys(Object.keys(this.stack))(this.dataReady)
        // this.svg.append('g')
        //   .selectAll('g')
        //   // Enter in the stack data = loop key per key = group per group
        //   .data(stackedData)
        //   .enter().append('g')
        //   .attr('class', function (d) { return 'bar-container-' + that.dataName })
        //   .attr('fill', function (d) { return that.color(d.key) })
        //   .selectAll('rect')
        //   // enter a second time = loop subgroup per subgroup to add all rectangles
        //   .data(function (d) { return d })
        //   .enter().append('rect')
        //   .attr('class', function (d) { return 'bar ' + that.dataName + 'bar-' + d.data.name })
        //   .attr('x', function (d) { return that.x(d.data.name) })
        //   .attr('y', function (d) { return that.y(d[1]) })
        //   .attr('height', function (d) { return that.y(d[0]) - that.y(d[1]) })
        //   .attr('width', that.x.bandwidth())
      } else {
        var u = this.svg.selectAll('rect').data(this.dataReady)

        u.enter()
          .append('rect')
          .merge(u)
          .attr('class', function (d) {
            return 'bar ' + that.dataName
          })
          .attr('x', function (d) {
            return that.x(d.name)
          })
          .attr('y', function (d) {
            return that.y(0)
          })
          .attr('rx', 3)
          .attr('ry', 3)
          .attr('width', that.x.bandwidth())
          .attr('height', function (d) {
            return that.height - that.y(0)
          })
          .attr('fill', primaryColor)
      }

      // Add tooltip
      this.addMouseTooltip(d3.selectAll(`.${this.dataName}.bar`))
    },
    onMouseOver(d, target) {
      const tooltip = d3.select(`.tooltip-bar-chart-container-${this.dataName}`)
      tooltip.transition().duration(200).style('opacity', 0.95)
      if (this.stack) {
        if (Object.keys(this.stack).length === 2) {
          // if there are two segments in this bar
          const val = target[1] - target[0]
          let key
          if (
            Object.keys(target.data).filter(
              (i) => this.stack[i] && target.data[i] > 0
            ).length === 2
          ) {
            const sum = Object.keys(target.data)
              .filter((k) => k !== 'name')
              .map((k) => target.data[k])
              .reduce((res, n) => {
                res += n
                return res
              }, 0)
            if (target[0] === 0) {
              key = Object.keys(this.stack)[0]
            } // low
            if (target[1] === sum) {
              key = Object.keys(this.stack)[1]
            } // high
          } else {
            // only 1 item
            key = Object.keys(target.data).find(
              (i) => this.stack[i] && target.data[i] > 0
            )
          }

          const title = this.stack[key].title
          tooltip.html(
            `<div class="p-2 tooltip-bg"><table><tr><td class="d-flex align-items-center"><i class="bi bi-people-fill fs-3 me-3"></i><span class="me-1">${title}:</span><span>${val}</span></td></tr></table></div>`
          )
        } else if (Object.keys(this.stack).length > 2) {
          // for many we just list all values
          const rows = Object.keys(this.stack)
            .filter((s) => target.data[s])
            .map((s) => {
              const v = target.data[s]

              return `<tr><td>${this.stack[s].title}:</td><td>${v}</td></tr>`
            })
            .join('')
          tooltip.html(
            `<div class="p-2 tooltip-bg"><table>${rows}</table> </div>`
          )
        } else {
          // 3 items
          const val = target[1] - target[0]
          const key = Object.keys(target.data).filter(
            (k) => target.data[k] === val
          )
          const title = this.stack[key].title
          tooltip.html(
            `<div class="p-2 tooltip-bg"><table><tr><td class="d-flex align-items-center"><i class="bi bi-people-fill fs-3 me-3"></i><span class="me-1">${title}:</span><span>${val}</span></td></tr></table></div>`
          )
        }
      } else {
        // not stacked
        const percentageValue = Object.keys(target).includes('value')
          ? Math.round((target.value * 100) / this.numberOfResponses)
          : null

        const percentageString = isFinite(percentageValue)
          ? `${percentageValue}%`
          : ''

        var s = this.options.useOnlyPercentage
          ? percentageString
          : this.$t(
              this.options.denotingAnswers
                ? 'categoryDetails.tooltip.barValueAnswers'
                : 'categoryDetails.tooltip.barValue',
              target.value
            ) + (percentageString ? ` (${percentageString})` : '')

        const title = d.title ? d.title : ''
        tooltip.html(
          `<div class="p-2 tooltip-bg"><p class="tooltip-title">${title}</p><table><tr><td class="d-flex align-items-start"><i class="bi bi-people-fill fs-3 me-3"></i>${s}</td></tr></table></div>`
        )
      }
    },
    onMouseMove(d) {
      const tooltip = document.getElementsByClassName(
        `tooltip-bar-chart-container-${this.dataName}`
      )[0]
      const marginTop = 10
      const tooltipBox = tooltip.getBoundingClientRect()
      const tooltipOffsetX = -tooltipBox.width / 2 // half tooltip
      const tooltipOffsetY = -tooltipBox.height - marginTop
      const left = d.pageX + tooltipOffsetX
      const top = d.pageY + tooltipOffsetY
      tooltip.style.left = left + 'px'
      tooltip.style.top = top + 'px'
    },
    onMouseLeave(d) {
      d3.select(`.tooltip-bar-chart-container-${this.dataName}`)
        .transition()
        .duration(this.transitionTime)
        .style('opacity', 0)
        .on('end', function () {
          d3.select(this).style('left', null).style('top', null)
        })
    },
    clearOldStack(oldStackKeys) {
      const that = this
      oldStackKeys.forEach(function (key, i) {
        const bar = that.svg.selectAll('.segmentedbar-' + key)
        bar.remove()
      })
    },
    updateChart() {
      const that = this
      // X axis
      this.x = d3
        .scaleBand()
        .range([0, this.width])
        .domain(
          this.dataReady.map(function (d) {
            return d.name
          })
        )
        .padding(0.5)
      this.svg.selectAll('.x.barchart-axis').call(d3.axisBottom(this.x))

      // Y axis
      this.y = d3.scaleLinear().domain(this.domain).range([this.height, 0])
      this.svg
        .selectAll('.y.barchart-axis')
        .attr('opacity', this.options.hideYAxis ? 0 : 1)
        .transition()
        .duration(this.transitionTime)
        .call(
          d3
            .axisLeft(this.y)
            .ticks(this.ticks)
            .tickFormat(function (d) {
              if (d >= 1000) {
                d = d / 1000 + 'K'
              }
              return d
            })
        )

      var yGrid = d3.scaleLinear().domain(this.domain).range([this.height, 0])
      this.svg
        .selectAll('.barchart-grid')
        .transition()
        .duration(this.transitionTime)
        .call(
          d3
            .axisLeft(yGrid)
            .ticks(this.ticks)
            .tickSize(-this.width)
            .tickFormat('')
        )

      // update bars
      if (this.stack) {
        const stackedData = d3.stack().keys(Object.keys(this.stack))(
          this.dataReady
        )
        Object.keys(this.stack).forEach(function (key, i) {
          const bar = that.svg
            .selectAll('.segmentedbar-' + key)
            .data(stackedData[i], function (d) {
              return d.data.name + '-' + key
            })

          bar
            .transition()
            .duration(that.transitionTime)
            .attr('x', function (d) {
              return that.x(d.data.name)
            })
            .attr('y', function (d) {
              return that.y(d[1])
            })
            .attr('height', function (d) {
              return that.y(d[0]) - that.y(d[1])
            })

          bar
            .enter()
            .append('rect')
            .attr('class', function (d) {
              return (
                'segmentedbar segmentedbar-' +
                key +
                ' segmentedbar-' +
                that.dataName
              )
            })
            .attr('x', function (d) {
              return that.x(d.data.name)
            })
            .attr('y', function (d) {
              return that.y(d[1])
            })
            .attr('width', that.x.bandwidth())
            .attr('height', function (d) {
              return that.y(d[0]) - that.y(d[1])
            })
            .attr('fill', function (d) {
              return that.color(key)
            })
        })

        this.addMouseTooltip(d3.selectAll('.segmentedbar-' + this.dataName))
      } else {
        if (this.dataReady.length) {
          var u = this.svg.selectAll('rect').data(this.dataReady)

          u.enter()
            .append('rect')
            .merge(u)
            .transition()
            .duration(this.transitiontime)
            .attr('height', function (d) {
              return that.height - that.y(d.value)
            })
            .attr('x', function (d) {
              return that.x(d.name)
            })
            .attr('y', function (d) {
              return that.y(d.value)
            })
            .attr('width', that.x.bandwidth())
            .attr('fill', function (d) {
              return that.colorForBarChartValue(d.name)
            })
          // remove bars for deleted data
          this.svg.selectAll('.bar').data(this.dataReady).exit().remove()
        } else {
          this.svg.selectAll('rect').remove()
        }
      }
    }
  }
}
</script>

<style lang="scss">
.bar-chart {
  display: inline-block;
  position: relative;
  width: 100%;
  padding-bottom: 59%; /* aspect ratio */
  vertical-align: top;
  overflow: hidden;
  &.rotated-x-axis {
    // overflow: visible;
  }
}
.tooltip-bg {
  background-color: var(--kt-input-bg) !important;
  border-radius: 5px;
  box-shadow: 2px 2px 2px var(--kt-table-border-color);
}
.svg-content-responsive {
  display: inline-block;
  position: absolute;
  top: 10px;
  left: 0;
}

.bar {
  border-radius: 15px;
}
.barchart-grid > path {
  stroke: none;
  fill: none;
}

.barchart-grid > .tick > line {
  opacity: 0.7;
  stroke: lightgray;
}

.barchart-axis > .tick > line {
  stroke: lightgray;
  color: white;
}
.barchart-axis > .tick > text {
  color: var(--kt-headings-color);
  font-weight: 300;
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
}

.barchart-axis > path {
  stroke: lightgray;
}

.large-font.barchart-axis > .tick > text {
  font-size: 1.4rem !important;
}
.normal-font.barchart-axis > .tick > text {
  font-size: 0.9rem !important;
}
.normal-font.many-options.barchart-axis > .tick > text {
  font-size: 0.8rem !important;
}
</style>
